import React, { useEffect, useRef, useState } from "react";

// Animated product visuals for the project cards. Each scene is a pure function of
// elapsed time t (seconds). Numbers drawn on a scene come from the sources listed in
// WEBSITE_SOURCES.md; scenes tagged "Schematic" depict the workflow, not measured data.

const C = { em: '#34d399', sky: '#38bdf8', vi: '#a78bfa', am: '#fcd34d', ro: '#fb7185', mu: '#737373', dim: '#2a2d33', tx: '#e5e5e5', sub: '#a3a3a3', node: '#16191e' };
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';
const TAU = Math.PI * 2;
const clamp = (x, a = 0, b = 1) => Math.min(b, Math.max(a, x));
const ease = x => { x = clamp(x); return x * x * (3 - 2 * x); };
const lerp = (a, b, k) => a + (b - a) * k;
const rng = seed => () => {
  seed = (seed + 0x6D2B79F5) | 0;
  let r = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
  return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
};

const T = ({ children, size = 8, fill = C.sub, ...p }) => <text fontFamily={MONO} fontSize={size} fill={fill} {...p}>{children}</text>;
const Svg = ({ children }) => <svg viewBox="0 0 400 200" className="block w-full h-auto" aria-hidden="true">{children}</svg>;

const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Advances t at ~30 fps while the card is on screen; hovering freezes the frame.
const useClock = (ref, still) => {
  const [t, setT] = useState(still);
  const paused = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || reducedMotion() || typeof IntersectionObserver === 'undefined') return;
    let raf = 0, seen = false, last = 0, acc = 0, drawn = 0;
    const step = now => {
      if (!seen) { raf = 0; return; }
      if (last && !paused.current) acc += Math.min(now - last, 100) / 1000;
      last = now;
      if (now - drawn > 32) { drawn = now; setT(acc); }
      raf = requestAnimationFrame(step);
    };
    const io = new IntersectionObserver(([e]) => {
      seen = e.isIntersecting;
      if (seen && !raf) { last = 0; raf = requestAnimationFrame(step); }
    }, { threshold: 0.15 });
    io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
  }, [ref]);
  return [t, paused];
};

// Canvas scenes draw in the same 400 x 200 coordinate space as the SVG scenes.
const Canvas = ({ t, draw, init }) => {
  const ref = useRef(null);
  const state = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c || !c.getContext) return;
    const w = c.clientWidth, h = c.clientHeight, dpr = window.devicePixelRatio || 1;
    if (!w || !h) return;
    if (c.width !== Math.round(w * dpr) || c.height !== Math.round(h * dpr)) {
      c.width = Math.round(w * dpr);
      c.height = Math.round(h * dpr);
    }
    const ctx = c.getContext('2d');
    ctx.setTransform(dpr * w / 400, 0, 0, dpr * h / 200, 0, 0);
    ctx.clearRect(0, 0, 400, 200);
    if (!state.current) state.current = init();
    draw(ctx, t, state.current);
  }, [t, draw, init]);
  return <canvas ref={ref} className="block w-full aspect-[2/1]" aria-hidden="true"/>;
};

/* ---------- PsyDyBench: README leaderboard, 34 models ranked by within-person variance error ---------- */

const PSY = [
  ['gemma-2-27b-it', 27, .043], ['gpt-5.5', null, .061], ['gemma-3-27b-it', 27, .065], ['qwen-2.5-coder-32b', 32, .090],
  ['claude-sonnet-4', null, .115], ['qwen3.5-27b', 27, .136], ['gpt-4.1', null, .140], ['llama-3.1-8b', 8, .145],
  ['qwen-2.5-72b', 72, .146], ['gemma-3-12b-it', 12, .147], ['mistral-small-3.2-24b', 24, .150], ['qwen3-14b', 14, .160],
  ['deepseek-v3.2', 671, .161], ['qwen3-30b-a3b-thinking', 30, .175], ['deepseek-r1-0528', 671, .186], ['gemini-2.5-flash', null, .196],
  ['qwen3-8b', 8, .206], ['r1-distill-llama-70b', 70, .210], ['llama-4-maverick', 400, .216], ['qwen3-30b-a3b-instruct', 30, .232],
  ['qwen3-32b', 32, .236], ['llama-3.2-3b', 3, .240], ['llama-3-8b', 8, .242], ['gemini-2.5-pro', null, .247],
  ['qwen3-235b-a22b', 235, .250], ['gpt-oss-20b', 21, .270], ['gpt-oss-120b', 117, .287], ['llama-3.2-1b', 1, .309],
  ['granite-4.1-8b', 8, .315], ['qwen-2.5-7b', 7, .325], ['llama-3.1-70b', 70, .354], ['phi-4', 14, .362],
  ['llama-3.3-70b', 70, .363], ['gemma-3-4b-it', 4, .427],
];

const PsyScene = ({ t }) => {
  const x = p => 40 + Math.log10(p) / Math.log10(700) * 170;
  const y = e => 22 + e / 0.45 * 140;
  const k = t < 2.6 ? 0 : Math.floor((t - 2.6) / 1.5) % PSY.length;
  const start = clamp(k - 2, 0, PSY.length - 7);
  const sx = r => 232 + r / 0.7 * 160;
  const band = ease((t - 0.5) / 1.5);
  return (
    <Svg>
      <line x1={40} y1={162} x2={212} y2={162} stroke={C.dim}/>
      <line x1={40} y1={18} x2={40} y2={162} stroke={C.dim}/>
      {[1, 10, 100].map(p => <T key={p} x={x(p)} y={173} textAnchor="middle" fill={C.mu}>{p}B</T>)}
      {[0, .2, .4].map(e => (
        <g key={e}>
          <line x1={36} x2={40} y1={y(e)} y2={y(e)} stroke={C.dim}/>
          <T x={33} y={y(e) + 3} textAnchor="end" fill={C.mu}>{e}</T>
        </g>
      ))}
      <T x={126} y={188} textAnchor="middle">parameters (log scale)</T>
      <T transform="translate(10 92) rotate(-90)" textAnchor="middle">variance error</T>
      <T x={46} y={14} fill={C.mu}>↑ closer to human</T>
      {PSY.map(([name, p, e], i) => {
        if (!p) return null;
        const a = ease((t - i * 0.06) / 0.4);
        const on = i === k;
        return <circle key={name} cx={x(p)} cy={lerp(y(e) - 18, y(e), a)} r={on ? 4.2 : 2.6} fill={on ? C.am : C.sky} opacity={on ? 1 : 0.7 * a}/>;
      })}
      {!PSY[k][1] && <T x={208} y={30} textAnchor="end" fill={C.am}>size undisclosed</T>}

      <T x={232} y={14} fill={C.mu}>RANK  MODEL</T>
      <T x={392} y={14} fill={C.mu} textAnchor="end">ERROR</T>
      <clipPath id="psy-list"><rect x={228} y={19} width={170} height={128}/></clipPath>
      <g clipPath="url(#psy-list)">
        <g style={{ transform: `translateY(${-start * 18}px)`, transition: 'transform 500ms ease' }}>
          {PSY.map(([name, , e], i) => (
            <g key={name} opacity={ease((t - i * 0.06) / 0.4)}>
              {i === k && <rect x={229} y={22 + i * 18} width={166} height={15} rx={3} fill="#ffffff" opacity={0.07}/>}
              <T x={234} y={32 + i * 18} fill={i === k ? C.am : C.mu} size={7.5}>#{i + 1}</T>
              <T x={254} y={32 + i * 18} fill={i === k ? C.tx : C.sub} size={7.5}>{name}</T>
              <T x={390} y={32 + i * 18} fill={i === k ? C.am : C.sub} size={7.5} textAnchor="end">{e.toFixed(3)}</T>
            </g>
          ))}
        </g>
      </g>

      <T x={232} y={162}>within-person |r|</T>
      <rect x={sx(0.2)} y={167} width={(sx(0.7) - sx(0.2)) * band} height={8} rx={2} fill={C.em} opacity={0.3}/>
      <line x1={232} x2={392} y1={175} y2={175} stroke={C.dim}/>
      <rect x={sx(0.023) - 1} y={164} width={2.5} height={13} fill={C.ro}/>
      <T x={233} y={188} fill={C.ro} size={7.5}>models 0.023</T>
      <T x={sx(0.45)} y={188} fill={C.em} size={7.5} textAnchor="middle">human 0.20–0.70</T>
    </Svg>
  );
};

/* ---------- Everval: the recorded steps printed by `everval demo` (0.3.1) ---------- */

const EV = [
  { n: '01', s: 'BASELINE', r: 1, h: 'e66c7229c27c' },
  { n: '02', s: 'CHANGED', r: 0, h: '87d02199a241' },
  { n: '03', s: 'CANDIDATE', r: 1, h: '0341063e4b85' },
  { n: '04', s: 'INVESTIGATE', r: 2, h: '3 regraded' },
  { n: '06', s: 'RETAIN', r: 3, h: 'chain verified' },
];
const EV_COLOR = [C.ro, C.em, C.am, C.sky];
const EV_GLYPH = ['✗', '✓', '↺', '⛓'];

const EvervalScene = ({ t }) => {
  const tc = t % 13;
  const cx = i => 47 + i * 76;
  const py = r => (r ? 32 : 78);
  const seg = i => ease((tc - 0.6 - i * 1.9) / 0.8);
  return (
    <Svg>
      <T x={12} y={14} fill={C.mu}>pass rate</T>
      {[['3/3', 32], ['0/3', 78]].map(([l, yy]) => (
        <g key={l}>
          <line x1={34} x2={222} y1={yy} y2={yy} stroke={C.dim} strokeDasharray="2 3"/>
          <T x={12} y={yy + 3} fill={C.mu}>{l}</T>
        </g>
      ))}
      {[0, 1].map(i => {
        const k = seg(i + 1);
        return k > 0 && <line key={i} x1={cx(i)} y1={py(EV[i].r)} x2={lerp(cx(i), cx(i + 1), k)} y2={lerp(py(EV[i].r), py(EV[i + 1].r), k)} stroke={i ? C.em : C.ro} strokeWidth={1.5}/>;
      })}
      {[0, 1, 2].map(i => seg(i) > 0 && <circle key={i} cx={cx(i)} cy={py(EV[i].r)} r={3.5 * seg(i)} fill={EV[i].r ? C.em : C.ro}/>)}

      {[['A configuration changes.', 1], ['A check fails.', 1], ['The evidence stays.', 4]].map(([line, i], j) => (
        <T key={line} x={240} y={36 + j * 20} size={9} fill={C.tx} opacity={ease((tc - 0.6 - i * 1.9 - (j === 1 ? 0.6 : 0)) / 0.5)}>{line}</T>
      ))}

      {EV.map((st, i) => {
        const k = seg(i);
        const x0 = 12 + i * 76;
        const active = k > 0 && seg(i + 1) === 0 && i < 4;
        return (
          <g key={st.n} opacity={0.35 + 0.65 * k}>
            <rect x={x0} y={98} width={70} height={66} rx={5} fill={C.node} stroke={active ? C.sky : C.dim}/>
            <T x={x0 + 6} y={110} fill={C.mu} size={7}>{st.n}</T>
            <T x={x0 + 6} y={121} fill={C.tx} size={7}>{st.s}</T>
            {[0, 1, 2].map(j => {
              const a = ease((tc - 0.8 - i * 1.9 - j * 0.25) / 0.3);
              return (
                <g key={j} opacity={a}>
                  <circle cx={x0 + 14 + j * 21} cy={138} r={6.5} fill={EV_COLOR[st.r]} opacity={0.18}/>
                  <T x={x0 + 14 + j * 21} y={141} size={8} fill={EV_COLOR[st.r]} textAnchor="middle">{EV_GLYPH[st.r]}</T>
                </g>
              );
            })}
            <T x={x0 + 6} y={157} fill={C.mu} size={6.5}>{st.h.slice(0, 14)}</T>
          </g>
        );
      })}
      {[0, 1, 2, 3].map(i => {
        const k = ease((tc - 8.4 - i * 0.25) / 0.3);
        return k > 0 && <line key={i} x1={82 + i * 76} x2={82 + i * 76 + 6 * k} y1={131} y2={131} stroke={C.sky} strokeWidth={1.5}/>;
      })}
      <T x={12} y={186} fill={C.mu}>Provider calls: 0 · Provider spend: $0.00</T>
    </Svg>
  );
};

/* ---------- MOSAIC: pause an organization, edit one message, compare the branches ---------- */

const MOSAIC = (() => {
  const centers = [[62, 72], [168, 72], [115, 146]];
  const nodes = [];
  centers.forEach(([cx, cy], team) => {
    for (let j = 0; j < 7; j++) {
      const a = j === 0 ? 0 : (j - 1) / 6 * TAU;
      const r = j === 0 ? 0 : 27;
      nodes.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a), team });
    }
  });
  const edges = [];
  for (let team = 0; team < 3; team++) {
    const b = team * 7;
    for (let j = 1; j < 7; j++) {
      edges.push([b, b + j]);
      edges.push([b + j, b + (j % 6) + 1]);
    }
  }
  edges.push([0, 7], [7, 14], [14, 0]);
  const src = 3;
  const dist = nodes.map(() => Infinity);
  dist[src] = 0;
  const queue = [src];
  while (queue.length) {
    const u = queue.shift();
    edges.forEach(([a, b]) => {
      const v = a === u ? b : b === u ? a : -1;
      if (v >= 0 && dist[v] === Infinity) { dist[v] = dist[u] + 1; queue.push(v); }
    });
  }
  return { nodes, edges, dist, src, probe: 10 };
})();

const MosaicScene = ({ t }) => {
  const L = 11, ti = 4;
  const tc = t % L;
  const { nodes, edges, dist, src, probe } = MOSAIC;
  const reached = i => tc > ti + dist[i] * 0.6;
  const px = 245 + clamp(tc / L) * 145;
  return (
    <Svg>
      <T x={10} y={14} fill={C.mu}>organization</T>
      {edges.map(([a, b], j) => <line key={j} x1={nodes[a].x} y1={nodes[a].y} x2={nodes[b].x} y2={nodes[b].y} stroke={C.dim}/>)}
      {edges.map(([a, b], j) => {
        const u = (t * 0.55 + j * 0.37) % 1;
        const [p, q] = j % 2 ? [a, b] : [b, a];
        const hot = reached(p);
        return <circle key={j} cx={lerp(nodes[p].x, nodes[q].x, u)} cy={lerp(nodes[p].y, nodes[q].y, u)} r={1.6} fill={hot ? C.vi : C.sky} opacity={0.85}/>;
      })}
      {nodes.map((n, i) => (
        <circle key={i} cx={n.x} cy={n.y} r={i % 7 === 0 ? 5.5 : 4} fill={reached(i) ? '#2e2445' : C.node} stroke={i === src && tc > ti && tc < ti + 1.5 ? C.am : reached(i) ? C.vi : i === probe ? C.tx : C.sky} strokeWidth={i === probe ? 1.6 : 1}/>
      ))}
      {tc > ti && tc < ti + 2.2 && <T x={nodes[src].x + 8} y={nodes[src].y - 8} fill={C.am} size={7.5}>edit message</T>}

      <T x={242} y={14} fill={C.mu}>inside agent {probe}</T>
      {Array.from({ length: 60 }, (_, c) => {
        const i = c % 10, j = Math.floor(c / 10);
        const v = 0.5 + 0.5 * Math.sin(t * 1.7 + i * 0.7 + j * 1.3) * Math.cos(t * 0.9 + i * 0.3);
        const shifted = reached(probe) && (i + j) % 3 === 0;
        return <rect key={c} x={243 + i * 14.6} y={20 + j * 12} width={13} height={10.5} rx={1.5} fill={shifted ? C.vi : C.sky} opacity={0.1 + 0.75 * v}/>;
      })}

      <T x={242} y={108} fill={C.mu}>experiment timeline</T>
      <line x1={245} y1={128} x2={300} y2={128} stroke={C.sub}/>
      <line x1={300} y1={128} x2={390} y2={128} stroke={C.sub}/>
      <T x={390} y={122} textAnchor="end" size={7}>baseline</T>
      {tc > ti && (
        <g opacity={ease((tc - ti) / 0.5)}>
          <path d="M300 128 L320 152 L390 152" fill="none" stroke={C.vi}/>
          <T x={390} y={164} textAnchor="end" size={7} fill={C.vi}>branch</T>
        </g>
      )}
      <circle cx={tc < ti ? lerp(245, 300, tc / ti) : px} cy={128} r={3} fill={C.tx}/>
      {tc > ti && <circle cx={px < 320 ? lerp(300, 320, (px - 300) / 20) : px} cy={px < 320 ? lerp(128, 152, (px - 300) / 20) : 152} r={3} fill={C.vi}/>}
      {tc > ti - 0.3 && tc < ti + 0.9 && <T x={300} y={118} textAnchor="middle" fill={C.am} size={7}>❚❚ pause · branch</T>}
      <T x={10} y={192} fill={C.mu}>organizations of 50 to 5,000 agents</T>
    </Svg>
  );
};

/* ---------- Generative Organizations: several hundred agents in a seeded social network ---------- */

const GEN_CENTERS = [[70, 58], [175, 48], [295, 58], [85, 138], [205, 134], [320, 138]];
const initGen = () => {
  const r = rng(7);
  const nodes = Array.from({ length: 300 }, (_, i) => {
    const [cx, cy] = GEN_CENTERS[i % 6];
    const a = r() * TAU, d = Math.sqrt(r()) * 30;
    return { x: cx + d * Math.cos(a) * 1.45, y: cy + d * Math.sin(a), ph: r(), c: i % 6 };
  });
  const edges = [];
  nodes.forEach((n, i) => {
    for (let k = 0; k < 2; k++) {
      let j = Math.floor(r() * 300);
      if (r() < 0.85) j = (j - (j % 6)) + n.c;
      if (j !== i && j < 300) edges.push({ a: i, b: j, ph: r() });
    }
  });
  return { nodes, edges };
};
const PHASE_COLORS = [C.sky, C.em, C.vi];
const drawGen = (ctx, t, { nodes, edges }) => {
  ctx.lineWidth = 0.5;
  ctx.strokeStyle = 'rgba(255,255,255,0.045)';
  ctx.beginPath();
  edges.forEach(({ a, b }) => { ctx.moveTo(nodes[a].x, nodes[a].y); ctx.lineTo(nodes[b].x, nodes[b].y); });
  ctx.stroke();
  edges.forEach(({ a, b, ph }) => {
    const u = (t / 6 + ph) % 1;
    if (u > 0.05) return;
    ctx.strokeStyle = `rgba(52,211,153,${(1 - u / 0.05) * 0.8})`;
    ctx.lineWidth = 0.9;
    ctx.beginPath();
    ctx.moveTo(nodes[a].x, nodes[a].y);
    ctx.lineTo(nodes[b].x, nodes[b].y);
    ctx.stroke();
  });
  nodes.forEach(n => {
    const u = (t / 9 + n.ph) % 1;
    const p = Math.floor(u * 3), within = (u * 3) % 1;
    const lit = within < 0.18;
    ctx.fillStyle = lit ? PHASE_COLORS[p] : '#52525b';
    ctx.beginPath();
    ctx.arc(n.x, n.y, lit ? 2.1 : 1.5, 0, TAU);
    ctx.fill();
  });
  ctx.font = `8px ${MONO}`;
  ctx.fillStyle = C.mu;
  ctx.fillText(`simulated day ${1 + (Math.floor(t / 1.5) % 30)}`, 8, 12);
  [['memory', C.sky], ['planning', C.em], ['reflection', C.vi]].forEach(([l, c], i) => {
    ctx.fillStyle = c;
    ctx.beginPath();
    ctx.arc(11 + i * 62, 189, 2.5, 0, TAU);
    ctx.fill();
    ctx.fillStyle = C.sub;
    ctx.fillText(l, 17 + i * 62, 192);
  });
  ctx.textAlign = 'right';
  ctx.fillText('100+ simulations · ≈48,000 agent-days', 394, 192);
  ctx.textAlign = 'left';
};
const GenOrgScene = ({ t }) => <Canvas t={t} draw={drawGen} init={initGen}/>;

/* ---------- Post-training: daily experience samples in, next-day forecast out ---------- */

const series = (k, d) => 0.5 + 0.22 * Math.sin(d * 0.9 + k * 2.1) + 0.14 * Math.sin(d * 2.3 + k * 0.7) + 0.08 * Math.sin(d * 5.1 + k);
const POST_COLORS = [C.sky, C.vi, C.em];

const PostScene = ({ t }) => {
  const d = t / 1.4;
  const frac = d % 1, base = Math.floor(d);
  const pulse = 0.5 + 0.5 * Math.sin(t * 4.5);
  return (
    <Svg>
      <T x={10} y={14} fill={C.mu}>daily experience samples</T>
      <rect x={10} y={22} width={132} height={128} rx={5} fill={C.node} stroke={C.dim}/>
      {[0, 1, 2].map(k => {
        const pts = Array.from({ length: 11 }, (_, j) => {
          const day = base + j - 9;
          return `${14 + (j - frac) * 13},${30 + k * 40 + (1 - series(k, day)) * 30}`;
        }).join(' ');
        return <polyline key={k} points={pts} fill="none" stroke={POST_COLORS[k]} strokeWidth={1.4} clipPath="url(#post-clip)"/>;
      })}
      <clipPath id="post-clip"><rect x={12} y={22} width={128} height={128}/></clipPath>
      {Array.from({ length: 6 }, (_, j) => {
        const u = (t * 0.8 + j / 6) % 1;
        return <circle key={j} cx={lerp(146, 186, u)} cy={86 + Math.sin(j * 2.3) * 22 * (1 - u)} r={1.8} fill={POST_COLORS[j % 3]} opacity={1 - u * 0.6}/>;
      })}

      <rect x={190} y={36} width={96} height={100} rx={6} fill={C.node} stroke={C.em} strokeOpacity={0.35 + 0.4 * pulse}/>
      <T x={238} y={50} textAnchor="middle" fill={C.tx} size={7.5}>Qwen3-4B-Base</T>
      <rect x={200} y={62} width={36} height={36} fill="#3f3f46" opacity={0.6}/>
      <T x={218} y={84} textAnchor="middle" fill={C.mu} size={7}>W</T>
      <T x={242} y={84} fill={C.sub} size={9}>+</T>
      <rect x={252} y={62} width={6} height={36} fill={C.em} opacity={0.5 + 0.5 * pulse}/>
      <rect x={262} y={62} width={18} height={6} fill={C.em} opacity={0.5 + 0.5 * pulse}/>
      <T x={238} y={113} textAnchor="middle" fill={C.mu} size={7}>frozen + LoRA</T>
      <T x={238} y={126} textAnchor="middle" fill={C.em} size={7}>4-bit QLoRA SFT</T>

      <T x={300} y={14} fill={C.mu}>next-day forecast</T>
      {[0, 1, 2].map(k => {
        const mu = series(k, base + 2);
        const cy = 40 + k * 40;
        const w = 0.35 + 0.15 * Math.sin(t + k);
        const path = Array.from({ length: 21 }, (_, j) => {
          const xv = j / 20;
          const yv = Math.exp(-((xv - mu) ** 2) / (2 * (w * 0.25) ** 2));
          return `${300 + xv * 88},${cy + 12 - yv * 20}`;
        }).join(' ');
        return (
          <g key={k}>
            <line x1={300} x2={388} y1={cy + 12} y2={cy + 12} stroke={C.dim}/>
            <polyline points={path} fill="none" stroke={POST_COLORS[k]} strokeWidth={1.4} style={{ transition: 'all 600ms ease' }}/>
          </g>
        );
      })}
      <T x={10} y={168} fill={C.mu}>67,555 examples · 2,042 participants</T>
      <T x={10} y={182} fill={C.mu}>public experience-sampling studies</T>
    </Svg>
  );
};

/* ---------- Scaling AI Workforces: 1,000 agents rearranged into organizational structures ---------- */

const LAYOUTS = ['independent agents', 'hierarchy', 'teams of 25'];
const initScale = () => {
  const r = rng(11);
  const levels = [[1, [0]], [9, [24]], [90, [46]], [900, [60, 65, 70, 75, 80]]];
  const hier = [];
  levels.forEach(([n, radii]) => {
    for (let j = 0; j < n; j++) {
      const a = (j + 0.5) / n * TAU - Math.PI / 2;
      const r = radii[j % radii.length];
      hier.push([200 + r * 2.1 * Math.cos(a), 100 + r * Math.sin(a)]);
    }
  });
  const agents = Array.from({ length: 1000 }, (_, i) => {
    const team = Math.floor(i / 25), m = i % 25;
    return {
      role: i === 0 ? 0 : i < 10 ? 1 : i < 100 ? 2 : 3,
      ph: r(),
      pos: [
        [20 + r() * 360, 24 + r() * 140],
        hier[i],
        [38 + (team % 10) * 36 + (m % 5) * 4.6 - 9, 40 + Math.floor(team / 10) * 34 + Math.floor(m / 5) * 4.6 - 9],
      ],
    };
  });
  return { agents };
};
const ROLE_COLORS = [C.am, C.sky, C.vi, 'rgba(163,163,163,0.75)'];
const drawScale = (ctx, t, { agents }) => {
  const P = 4.5;
  const idx = Math.floor(t / P) % 3, prev = (idx + 2) % 3, tc = t % P;
  agents.forEach((a, i) => {
    const k = t < P ? 1 : ease((tc - (i % 50) * 0.008) / 1.0);
    const x = lerp(a.pos[prev][0], a.pos[idx][0], k);
    const y = lerp(a.pos[prev][1], a.pos[idx][1], k);
    const flash = (t * 0.7 + a.ph * 40) % 40 < 0.25;
    ctx.fillStyle = flash ? C.em : ROLE_COLORS[a.role];
    const s = flash ? 2.6 : a.role < 3 ? 2.2 : 1.5;
    ctx.fillRect(x - s / 2, y - s / 2, s, s);
  });
  ctx.font = `8px ${MONO}`;
  ctx.fillStyle = C.mu;
  ctx.fillText(`1,000 agents · ${LAYOUTS[idx]}`, 8, 12);
  ctx.fillStyle = C.em;
  ctx.fillRect(8, 186, 3, 3);
  ctx.fillStyle = C.sub;
  ctx.fillText('126 theorem-proving swarms · 46,595 Lean-verified submissions', 15, 190);
};
const ScaleScene = ({ t }) => <Canvas t={t} draw={drawScale} init={initScale}/>;

/* ---------- Reflex: session history to repeated workflows to skills, then routing ---------- */

const REFLEX = (() => {
  const r = rng(5);
  return Array.from({ length: 42 }, (_, i) => ({
    src: i % 3 === 2 && r() < 0.6 ? 0 : i % 3,
    x: 14 + r() * 106, y: 26 + r() * 146,
    c: Math.floor(r() * 3),
    ox: (r() - 0.5) * 34, oy: (r() - 0.5) * 22,
  }));
})();
const SRC_COLORS = [C.am, C.sky, C.vi];
const CLUSTERS = [[188, 46, 'release'], [188, 104, 'test-fix'], [188, 160, 'refactor']];

const ReflexScene = ({ t }) => {
  const tc = t % 12;
  const skill = ease((tc - 4.6) / 1.2);
  const task = ease((tc - 7.4) / 0.6);
  const route = tc > 9.2;
  return (
    <Svg>
      <T x={10} y={14} fill={C.mu}>sessions</T>
      <T x={150} y={14} fill={C.mu}>repeated workflows</T>
      <T x={262} y={14} fill={C.mu}>skills · routing</T>
      {CLUSTERS.map(([x, y, l], c) => (
        <g key={l} opacity={ease((tc - 2.6) / 0.6)}>
          <ellipse cx={x} cy={y} rx={30} ry={20} fill="none" stroke={c === 0 && tc > 4.2 && tc < 6.4 ? C.em : C.dim} strokeDasharray="3 3"/>
          <T x={x} y={y + 30} textAnchor="middle" size={7} fill={C.sub}>{l}</T>
        </g>
      ))}
      {task > 0 && REFLEX.filter(s => s.c === 1).slice(0, 4).map((s, i) => (
        <line key={i} x1={330} y1={128} x2={CLUSTERS[1][0] + s.ox} y2={CLUSTERS[1][1] + s.oy} stroke={C.sky} strokeOpacity={0.5 * task} strokeDasharray="2 2"/>
      ))}
      {REFLEX.map((s, i) => {
        const k = ease((tc - 0.4 - i * 0.04) / 1.8);
        const [cx, cy] = CLUSTERS[s.c];
        return <circle key={i} cx={lerp(s.x, cx + s.ox, k)} cy={lerp(s.y, cy + s.oy, k)} r={2.6} fill={SRC_COLORS[s.src]} opacity={0.85}/>;
      })}

      <g opacity={skill} transform={`translate(${lerp(-60, 0, skill)} 0)`}>
        <rect x={262} y={26} width={128} height={56} rx={5} fill={C.node} stroke={C.em}/>
        <T x={270} y={40} fill={C.em} size={7.5}>SKILL.md · release</T>
        {[0, 1, 2].map(j => <rect key={j} x={270} y={48 + j * 9} width={[96, 80, 104][j]} height={3.5} rx={1.5} fill={C.dim}/>)}
        <T x={386} y={95} textAnchor="end" size={7}>→ ~/.claude/skills</T>
      </g>

      <g opacity={task}>
        <rect x={300} y={118} width={60} height={20} rx={4} fill={C.node} stroke={C.sky}/>
        <T x={330} y={131} textAnchor="middle" fill={C.tx} size={7.5}>new task</T>
      </g>
      {['large', 'mid', 'small'].map((m, i) => {
        const pick = route && i === 2;
        return (
          <g key={m} opacity={task}>
            <rect x={268 + i * 42} y={150} width={36} height={16} rx={8} fill={pick ? C.em : C.node} fillOpacity={pick ? 0.2 : 1} stroke={pick ? C.em : C.dim}/>
            <T x={286 + i * 42} y={161} textAnchor="middle" size={7} fill={pick ? C.em : C.sub}>{m}</T>
          </g>
        );
      })}
      {tc > 10.2 && <T x={330} y={182} textAnchor="middle" size={7} fill={C.em} opacity={ease((tc - 10.2) / 0.4)}>bench · tests pass ✓</T>}
      {[['Claude Code', 0, 13], ['Codex', 1, 78], ['Cursor', 2, 120]].map(([l, c, x]) => (
        <g key={l}>
          <circle cx={x} cy={189} r={2.5} fill={SRC_COLORS[c]}/>
          <T x={x + 6} y={192} size={7}>{l}</T>
        </g>
      ))}
    </Svg>
  );
};

/* ---------- CEO: delegations down the org chart, every event appended to a hash-chained ledger ---------- */

const ROLES = ['research', 'build', 'review', 'audit'];
const CEO_EVENTS = [
  { a: 1, k: 'delegate', at: 0.5, h: 'a3f1' },
  { a: 0, k: 'delegate', at: 1.6, h: '7c0e' },
  { a: 1, k: 'result', at: 3.0, h: 'e912' },
  { a: 2, k: 'delegate', at: 4.0, h: '41bd' },
  { a: 1, k: 'denied', at: 5.2, h: '0f6a' },
  { a: 2, k: 'result', at: 6.4, h: 'c27d' },
  { a: 3, k: 'audit', at: 7.4, h: '9b30' },
];
const ceoLabel = e => e.k === 'delegate' ? `delegate → ${ROLES[e.a]}` : e.k === 'result' ? `result ← ${ROLES[e.a]}` : e.k === 'denied' ? `denied · git push` : 'audit · chain ✓';
const ceoColor = e => e.k === 'denied' ? C.ro : e.k === 'audit' ? C.em : e.k === 'result' ? C.sky : C.sub;

const CeoScene = ({ t }) => {
  const tc = t % 10;
  const ax = i => 30 + i * 55;
  return (
    <Svg>
      <T x={10} y={14} fill={C.mu}>org</T>
      <rect x={88} y={22} width={46} height={18} rx={4} fill={C.node} stroke={C.am}/>
      <T x={111} y={34} textAnchor="middle" fill={C.am} size={8}>CEO</T>
      {ROLES.map((r, i) => (
        <g key={r}>
          <path d={`M111 40 C111 70 ${ax(i) + 22} 70 ${ax(i) + 22} 98`} fill="none" stroke={C.dim}/>
          <rect x={ax(i)} y={98} width={44} height={18} rx={4} fill={C.node} stroke={C.dim}/>
          <T x={ax(i) + 22} y={110} textAnchor="middle" size={7} fill={C.tx}>{r}</T>
          <rect x={ax(i)} y={124} width={44} height={4} rx={2} fill={C.dim}/>
          {(() => { const f = (t * 0.07 + i * 0.27) % 1; return <rect x={ax(i)} y={124} width={44 * f} height={4} rx={2} fill={f > 0.8 ? C.am : C.em}/>; })()}
        </g>
      ))}
      <T x={10} y={142} fill={C.mu} size={7}>budget per agent</T>
      {CEO_EVENTS.map((e, i) => {
        const u = (tc - e.at) / 0.8;
        if (u < 0 || u > 1 || e.k === 'audit') return null;
        const k = ease(u);
        const down = e.k !== 'result';
        const [y0, y1] = down ? [40, 98] : [98, 40];
        const yy = lerp(y0, y1, k);
        const s = (yy - 40) / 58;
        const xx = lerp(111, ax(e.a) + 22, s * s * (3 - 2 * s));
        return e.k === 'denied' && k > 0.8
          ? <T key={i} x={ax(e.a) + 22} y={94} textAnchor="middle" fill={C.ro} size={10}>✗</T>
          : <circle key={i} cx={xx} cy={yy} r={3} fill={ceoColor(e)}/>;
      })}

      <T x={232} y={14} fill={C.mu}>ledger · hash-chained</T>
      {CEO_EVENTS.filter(e => tc > e.at).map((e, i, arr) => (
        <g key={e.h} opacity={ease((tc - e.at) / 0.4)}>
          {i > 0 && <line x1={238} x2={238} y1={10 + i * 21} y2={24 + i * 21} stroke={C.sky} strokeOpacity={0.5}/>}
          <circle cx={238} cy={28 + i * 21} r={3} fill="none" stroke={C.sky}/>
          <T x={246} y={31 + i * 21} size={7} fill={C.mu}>{e.h}</T>
          <T x={270} y={31 + i * 21} size={7.5} fill={ceoColor(e)}>{ceoLabel(e)}</T>
        </g>
      ))}
      <T x={10} y={186} fill={C.mu} size={7}>claude -p · codex exec · tool allowlist · sandbox</T>
    </Svg>
  );
};

/* ---------- OrgSim-Gym: one agent, six simulated coworkers, decomposed reward ---------- */

const OrgSimScene = ({ t }) => {
  const cw = Array.from({ length: 6 }, (_, i) => {
    const a = i / 6 * TAU - Math.PI / 2;
    return {
      x: 105 + 68 * Math.cos(a), y: 100 + 68 * Math.sin(a),
      trust: 0.5 + 0.35 * Math.sin(t * 0.5 + i),
      stress: 0.5 + 0.45 * Math.sin(t * 0.7 + i * 1.7 + 1),
    };
  });
  const k = Math.floor(t / 2.4) % 6;
  const reported = 0.55 + 0.25 * Math.sin(t * 0.3 + k);
  const actual = reported - (k % 3 === 0 ? 0.3 : 0.04);
  const step = Math.floor(t * 2);
  return (
    <Svg>
      <T x={10} y={14} fill={C.mu}>agent and coworkers</T>
      {cw.map((c, i) => (
        <g key={i}>
          <line x1={105} y1={100} x2={c.x} y2={c.y} stroke={i === k ? C.sky : C.dim}/>
          {(() => { const u = (t * 0.6 + i * 0.3) % 1; return <circle cx={lerp(c.x, 105, u)} cy={lerp(c.y, 100, u)} r={1.6} fill={C.sky} opacity={0.7}/>; })()}
        </g>
      ))}
      <circle cx={105} cy={100} r={15} fill="#0f2a22" stroke={C.em}/>
      <T x={105} y={103} textAnchor="middle" fill={C.em} size={7.5}>agent</T>
      {cw.map((c, i) => (
        <g key={i}>
          <circle cx={c.x} cy={c.y} r={9} fill={C.node} stroke={c.stress > 0.85 ? C.ro : i === k ? C.sky : C.sub} strokeWidth={c.stress > 0.85 ? 1.8 : 1}/>
          <rect x={c.x + 12} y={c.y - 8 + 14 * (1 - c.trust)} width={3} height={14 * c.trust} fill={C.em}/>
          <rect x={c.x + 17} y={c.y - 8 + 14 * (1 - c.stress)} width={3} height={14 * c.stress} fill={C.ro}/>
        </g>
      ))}
      <T x={10} y={192} size={7} fill={C.em}>▮ trust</T>
      <T x={52} y={192} size={7} fill={C.ro}>▮ stress</T>

      <T x={240} y={14} fill={C.mu}>progress report · coworker {k + 1}</T>
      {[['reported', reported, C.sky], ['actual', actual, C.mu]].map(([l, v, c], i) => (
        <g key={l}>
          <T x={240} y={34 + i * 18} size={7}>{l}</T>
          <rect x={286} y={27 + i * 18} width={104} height={8} rx={2} fill={C.dim}/>
          <rect x={286} y={27 + i * 18} width={104 * v} height={8} rx={2} fill={c} style={{ transition: 'width 500ms ease' }}/>
        </g>
      ))}
      {k % 3 === 0 && <T x={390} y={72} textAnchor="end" size={7} fill={C.am}>inflated</T>}

      <T x={240} y={96} fill={C.mu}>reward, by term</T>
      {Array.from({ length: 24 }, (_, j) => {
        const s = step - 23 + j;
        const v = [0.45 + 0.3 * Math.sin(s * 0.4), 0.35 + 0.3 * Math.sin(s * 0.23 + 2), 0.3 + 0.25 * Math.sin(s * 0.31 + 4)];
        let y0 = 176;
        return (
          <g key={j}>
            {v.map((h, q) => { const hh = h * 22; y0 -= hh; return <rect key={q} x={242 + j * 6.1} y={y0} width={4.8} height={hh} fill={[C.sky, C.am, C.em][q]} opacity={0.8}/>; })}
          </g>
        );
      })}
      {[['task', C.sky], ['honesty', C.am], ['wellbeing', C.em]].map(([l, c], i) => <T key={l} x={240 + i * 46} y={192} size={7} fill={c}>{l}</T>)}
    </Svg>
  );
};

/* ---------- Robots: foot-IMU gait drives a simulated walker; bars show the reported results ---------- */

const gaitPulse = ph => clamp(1 - Math.abs(((ph / TAU) % 1 + 1) % 1 - 0.75) * 9);

const RobotScene = ({ t }) => {
  const phi = t * TAU * 0.85;
  const rel = s => {
    const ph = phi + s * Math.PI;
    const a = 0.45 * Math.sin(ph);
    const b = 1.0 * clamp(Math.cos(ph + 0.6));
    const knee = [22 * Math.sin(a), 22 * Math.cos(a)];
    return { knee, foot: [knee[0] + 23 * Math.sin(a - b), knee[1] + 23 * Math.cos(a - b)], pulse: gaitPulse(ph) };
  };
  const legs = [rel(0), rel(1)];
  const hip = [92, 121 - Math.max(legs[0].foot[1], legs[1].foot[1])];
  const leg = s => {
    const { knee, foot, pulse } = legs[s];
    return { knee: [hip[0] + knee[0], hip[1] + knee[1]], foot: [hip[0] + foot[0], hip[1] + foot[1]], pulse };
  };
  const sh = [hip[0] + 3, hip[1] - 32];
  const arm = s => {
    const a = -0.35 * Math.sin(phi + s * Math.PI);
    const el = [sh[0] + 16 * Math.sin(a), sh[1] + 16 * Math.cos(a)];
    return [el, [el[0] + 15 * Math.sin(a + 0.4), el[1] + 15 * Math.cos(a + 0.4)]];
  };
  const grow = ease((t - 0.4) / 1.6);
  const trace = s => Array.from({ length: 60 }, (_, j) => {
    const ph = (t - (59 - j) * 0.03) * TAU * 0.85 + s * Math.PI;
    const v = 0.3 * Math.sin(ph) + gaitPulse(ph) * 0.9;
    return `${10 + j * 3},${168 + s * 16 - v * 9}`;
  }).join(' ');
  return (
    <Svg>
      <line x1={10} x2={190} y1={124} y2={124} stroke={C.dim}/>
      {Array.from({ length: 10 }, (_, j) => {
        const x = 10 + (((j * 20 - t * 38) % 200) + 200) % 200;
        return x < 190 && <line key={j} x1={x} x2={x + 7} y1={127} y2={127} stroke={C.dim}/>;
      })}
      {[1, 0].map(s => {
        const { knee, foot, pulse } = leg(s);
        const col = s ? C.mu : C.tx;
        const [el, hand] = arm(1 - s);
        return (
          <g key={s} stroke={col} strokeWidth={3} strokeLinecap="round" fill="none">
            <polyline points={`${sh[0]},${sh[1]} ${el[0]},${el[1]} ${hand[0]},${hand[1]}`} strokeWidth={2.4}/>
            <polyline points={`${hip[0]},${hip[1]} ${knee[0]},${knee[1]} ${foot[0]},${foot[1]} ${foot[0] + 8},${foot[1]}`}/>
            <rect x={foot[0] - 1} y={foot[1] - 4} width={6} height={4} fill={s ? C.vi : C.sky} stroke="none"/>
            <circle cx={foot[0] + 2} cy={foot[1] - 2} r={3 + 7 * pulse} stroke={s ? C.vi : C.sky} strokeWidth={1} opacity={pulse * 0.8}/>
          </g>
        );
      })}
      <line x1={hip[0]} y1={hip[1]} x2={sh[0]} y2={sh[1]} stroke={C.tx} strokeWidth={3} strokeLinecap="round"/>
      <circle cx={sh[0] + 1} cy={sh[1] - 9} r={6} fill="none" stroke={C.tx} strokeWidth={2.4}/>
      <T x={10} y={14} fill={C.mu}>gait prior from bilateral foot IMUs</T>
      <polyline points={trace(0)} fill="none" stroke={C.sky} strokeWidth={1.2}/>
      <polyline points={trace(1)} fill="none" stroke={C.vi} strokeWidth={1.2}/>

      <T x={212} y={30} fill={C.mu}>upright steps (of 500, 3 seeds)</T>
      {[['smart-shoe prior', 496.1, C.em], ['motion-capture prior', 499.1, C.sky]].map(([l, v, c], i) => (
        <g key={l}>
          <T x={212} y={54 + i * 36} size={7.5} fill={C.tx}>{l}</T>
          <T x={390} y={54 + i * 36} size={7.5} fill={c} textAnchor="end">{(v * grow).toFixed(1)}</T>
          <rect x={212} y={60 + i * 36} width={178} height={9} rx={2} fill={C.dim}/>
          <rect x={212} y={60 + i * 36} width={178 * v / 500 * grow} height={9} rx={2} fill={c} opacity={0.85}/>
        </g>
      ))}
      <T x={212} y={140} size={7.5}>8 wearers · 137 wearer-days</T>
      <T x={212} y={156} size={7.5}>free-living data, simulated humanoid</T>
    </Svg>
  );
};

/* ---------- ARENA: 35 student teams delegate to Claude agents, then submit to a buyer panel ---------- */

const ARENA_PH = (() => { const r = rng(3); return Array.from({ length: 35 }, () => r()); })();

const ArenaScene = ({ t }) => (
  <Svg>
    <T x={10} y={14} fill={C.mu}>35 student teams</T>
    <T x={390} y={14} fill={C.mu} textAnchor="end">simulated buyer panel</T>
    {ARENA_PH.map((ph, i) => {
      const u = (t / 9 + ph) % 1;
      const x = 12 + (i % 7) * 30, y = 24 + Math.floor(i / 7) * 27;
      const working = u > 0.35 && u < 0.7;
      const sending = u >= 0.7 && u < 0.86;
      const done = u >= 0.7;
      return (
        <g key={i}>
          <rect x={x} y={y} width={26} height={22} rx={3} fill={done ? '#0f2a22' : C.node} stroke={working ? C.sky : done ? C.em : C.dim}/>
          {working && [0, 1, 2].map(j => <circle key={j} cx={x + 7 + j * 6} cy={y + 11} r={1.6} fill={C.sky} opacity={0.4 + 0.6 * ((Math.sin(t * 6 + j + i) + 1) / 2)}/>)}
          {done && !sending && <T x={x + 13} y={y + 14} textAnchor="middle" size={8} fill={C.em}>✓</T>}
          {sending && (() => {
            const k = ease((u - 0.7) / 0.16);
            const by = 40 + (i % 5) * 28;
            return <circle cx={lerp(x + 13, 340, k)} cy={lerp(y + 11, by, k)} r={2.2} fill={C.em}/>;
          })()}
        </g>
      );
    })}
    {[0, 1, 2, 3, 4].map(j => {
      const hit = ARENA_PH.some((ph, i) => i % 5 === j && (((t / 9 + ph) % 1) - 0.86) > 0 && (((t / 9 + ph) % 1) - 0.86) < 0.02);
      return (
        <g key={j}>
          <circle cx={350} cy={40 + j * 28} r={9} fill={C.node} stroke={hit ? C.em : C.sub} strokeWidth={hit ? 1.8 : 1}/>
          <circle cx={350} cy={37 + j * 28} r={2.5} fill={C.sub}/>
          <path d={`M344 ${46 + j * 28} q6 -6 12 0`} fill="none" stroke={C.sub}/>
        </g>
      );
    })}
    <rect x={12} y={168} width={7} height={7} rx={1.5} fill={C.node} stroke={C.sky}/>
    <T x={23} y={175} size={7}>delegating to Claude agents</T>
    <rect x={150} y={168} width={7} height={7} rx={1.5} fill="#0f2a22" stroke={C.em}/>
    <T x={161} y={175} size={7}>submitted</T>
    <T x={10} y={193} fill={C.mu} size={7.5}>stress test · 35 teams · 100 students · 60 min · p95 237 ms</T>
  </Svg>
);

/* ---------- Frame ---------- */

const SCENES = {
  psydybench: { title: 'leaderboard · within-person variance error', tag: 'Released results', real: true, still: 9, Scene: PsyScene },
  everval: { title: 'everval demo · recorded output', tag: 'Real output', real: true, still: 11, Scene: EvervalScene },
  mosaic: { title: 'intervene · branch · compare', tag: 'Schematic', still: 6, Scene: MosaicScene },
  genorg: { title: 'seeded social network of agents', tag: 'Schematic', still: 4, Scene: GenOrgScene },
  posttrain: { title: 'QLoRA SFT on daily experience samples', tag: 'Schematic', still: 3, Scene: PostScene },
  scaling: { title: 'swarm structures', tag: 'Schematic', still: 6, Scene: ScaleScene },
  reflex: { title: 'reflex · sessions → skills → routing', tag: 'Schematic', still: 11, Scene: ReflexScene },
  ceo: { title: 'ceo · delegations and ledger', tag: 'Schematic', still: 8, Scene: CeoScene },
  orgsim: { title: 'OrgSim-Gym · episode view', tag: 'Schematic', still: 5, Scene: OrgSimScene },
  robots: { title: 'IMU gait prior → simulated humanoid', tag: 'Reported results', real: true, still: 3, Scene: RobotScene },
  arena: { title: 'ARENA · classroom session', tag: 'Schematic', still: 4, Scene: ArenaScene },
};

export default function CardViz({ kind, flat }) {
  const ref = useRef(null);
  const s = SCENES[kind];
  const [t, paused] = useClock(ref, s ? s.still : 0);
  if (!s) return null;
  const { Scene } = s;
  return (
    <div className={flat ? 'min-w-0' : 'min-w-0 rounded-xl p-px bg-gradient-to-br from-emerald-500/40 via-neutral-800 to-sky-500/40 shadow-lg shadow-emerald-950/20'}>
      <div
        ref={ref}
        className={flat ? 'bg-[#0b0d10] overflow-hidden' : 'rounded-[11px] bg-[#0b0d10] overflow-hidden'}
        onMouseEnter={() => { paused.current = true; }}
        onMouseLeave={() => { paused.current = false; }}
      >
        <div className="flex items-center gap-2 px-3 py-1.5 border-b border-white/5 bg-white/[0.02]">
          <span className="truncate whitespace-nowrap text-[11px] font-mono text-neutral-500">{s.title}</span>
          <span className={`ml-auto shrink-0 whitespace-nowrap rounded-full px-2 py-px text-[10px] font-mono ${s.real ? 'text-emerald-300 bg-emerald-400/10' : 'text-neutral-400 bg-white/5'}`}>{s.tag}</span>
        </div>
        <div className="px-1.5 py-1.5"><Scene t={t}/></div>
      </div>
    </div>
  );
}
